import { Link } from "react-router-dom";
import { Flame, Snowflake } from "lucide-react";
import { fmtOdds } from "@/lib/odds";
import { TeamFollowButton } from "@/components/TeamFollowButton";

const DOT = { W: "bg-emerald-400", L: "bg-red-400", D: "bg-slate-500" };

export function FormDots({ form = [], size = "h-2.5 w-2.5" }) {
  if (!form.length) return <span className="text-xs text-slate-600">—</span>;
  return (
    <span className="inline-flex items-center gap-1" data-testid="form-dots" title={form.join(" ")}>
      {form.map((r, i) => <span key={i} className={`${size} rounded-full ${DOT[r]} ${i === form.length - 1 ? "ring-2 ring-white/20" : ""}`} />)}
    </span>
  );
}

export function HeatBadge({ heat }) {
  if (!heat) return null;
  const hot = heat === "hot";
  return (
    <span data-testid={`heat-${heat}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${hot ? "bg-orange-500/15 text-orange-300" : "bg-sky-500/15 text-sky-300"}`}>
      {hot ? <Flame size={10} /> : <Snowflake size={10} />} {hot ? "Hot" : "Cold"}
    </span>
  );
}

export function StreakChipText({ streak }) {
  const c = streak?.startsWith("W") ? "text-emerald-400" : streak?.startsWith("L") ? "text-red-400" : "text-slate-400";
  return <span className={`font-mono-num text-xs font-bold ${c}`}>{streak}</span>;
}

export const teamSlug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export function TeamLink({ sport, team, className = "" }) {
  return <Link to={`/teams/${sport}/${teamSlug(team)}`} data-testid="team-link" className={`transition-colors hover:text-emerald-300 hover:underline ${className}`}>{team}</Link>;
}

export function StandingsTable({ d, highlight = [], compact = false }) {
  const pts = !!d.points_system;
  const solo = d.kind === "rankings";
  const th = "px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500";
  const num = "font-mono-num px-3 py-2 text-right text-sm";
  return (
    <div className="glass-card overflow-x-auto" data-testid="standings-table">
      <table className="w-full min-w-[560px]">
        <thead className="border-b border-[#26334D]">
          <tr>
            <th className={th}>#</th>
            <th className={th}>{solo ? "Player" : "Team"}</th>
            <th className={`${th} text-right`}>GP</th>
            <th className={`${th} text-right`}>W</th>
            <th className={`${th} text-right`}>L</th>
            {d.draws && <th className={`${th} text-right`}>D</th>}
            <th className={`${th} text-right`}>{pts ? "Pts" : "Pct"}</th>
            {!compact && <th className={`${th} text-right`}>+/-</th>}
            {!compact && <th className={`${th} text-right`}>Home</th>}
            {!compact && <th className={`${th} text-right`}>Away</th>}
            <th className={th}>Form</th>
            <th className={`${th} text-right`}>Strk</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#26334D]/60">
          {d.rows.map((r) => {
            const hl = highlight.includes(r.team);
            return (
              <tr key={r.team} data-testid="standings-row" className={`transition-colors duration-150 ${hl ? "bg-emerald-500/10" : "hover:bg-[#182030]"}`}>
                <td className="font-mono-num px-3 py-2 text-sm text-slate-500">{r.rank}</td>
                <td className="px-3 py-2 text-sm">
                  <span className={`flex items-center gap-2 font-semibold ${hl ? "text-emerald-300" : "text-white"}`}><TeamFollowButton sport={d.sport} slug={teamSlug(r.team)} team={r.team} size="icon" /><TeamLink sport={d.sport} team={r.team} /><HeatBadge heat={r.heat} /></span>
                </td>
                <td className={`${num} text-slate-400`}>{r.gp}</td>
                <td className={`${num} text-white`}>{r.w}</td>
                <td className={`${num} text-white`}>{r.l}</td>
                {d.draws && <td className={`${num} text-slate-300`}>{r.d}</td>}
                <td className={`${num} font-bold text-amber-400`}>{pts ? r.pts : r.pct.toFixed(3).replace(/^0/, "")}</td>
                {!compact && <td className={`${num} ${r.diff > 0 ? "text-emerald-400" : r.diff < 0 ? "text-red-400" : "text-slate-400"}`}>{r.diff > 0 ? "+" : ""}{r.diff}</td>}
                {!compact && <td className={`${num} text-xs text-slate-400`}>{r.home}</td>}
                {!compact && <td className={`${num} text-xs text-slate-400`}>{r.away}</td>}
                <td className="px-3 py-2"><FormDots form={r.form} /></td>
                <td className="px-3 py-2 text-right"><StreakChipText streak={r.streak} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function TeamFormCard({ row, side, total, pts, sport }) {
  if (!row) return null;
  return (
    <div data-testid={`form-card-${side}`} className="glass-card min-w-0 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">{side}</p>
          <p className="truncate font-display text-lg font-bold text-white">{sport ? <TeamLink sport={sport} team={row.team} /> : row.team}</p>
        </div>
        <span className="flex items-center gap-1"><HeatBadge heat={row.heat} />{sport && <TeamFollowButton sport={sport} slug={teamSlug(row.team)} team={row.team} size="icon" />}</span>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-x-4 gap-y-1">
        <span className="font-mono-num text-2xl font-black text-white" data-testid={`form-rank-${side}`}>#{row.rank}<span className="text-sm font-normal text-slate-500"> / {total}</span></span>
        <span className="font-mono-num text-sm text-slate-300">{row.w}–{row.l}{row.d ? `–${row.d}` : ""}</span>
        <span className="font-mono-num text-sm text-amber-400">{pts ? `${row.pts} pts` : row.pct.toFixed(3).replace(/^0/, "")}</span>
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
        <span className="flex items-center gap-2">Last 5 <FormDots form={row.form} /></span>
        <span>Streak <StreakChipText streak={row.streak} /></span>
      </div>
      {row.last_game && <p className="mt-2 truncate text-[11px] text-slate-500">Last: <span className={row.last_game.result === "W" ? "text-emerald-400" : row.last_game.result === "L" ? "text-red-400" : "text-slate-300"}>{row.last_game.result} {row.last_game.score}</span> {row.last_game.at_home ? "vs" : "@"} {row.last_game.opponent}</p>}
    </div>
  );
}

export function MatchupForm({ s, sport }) {
  if (!s || !s.home || !s.away) return null;
  const { home, away, series, h2h } = s;
  return (
    <div data-testid="game-standings">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Standings & form</h2>
        <Link to={`/standings?sport=${sport}`} data-testid="game-full-table-link" className="text-xs font-semibold uppercase tracking-wider text-emerald-400 hover:text-emerald-300">Full {s.league} {s.kind === "rankings" ? "rankings" : "table"} →</Link>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto_1fr] md:items-stretch">
        <TeamFormCard row={away} side="away" total={s.teams_total} pts={!!s.points_system} sport={sport} />
        <div className="glass-card flex flex-col items-center justify-center px-4 py-3 text-center" data-testid="game-h2h">
          <p className="label-caps">Season series</p>
          <p className="font-mono-num mt-1 text-2xl font-black text-white">{series.away}<span className="text-slate-600"> – </span>{series.home}{series.draws ? <span className="text-sm text-slate-500"> ({series.draws} D)</span> : null}</p>
          <p className="mt-1 text-[11px] text-slate-500">{h2h.length ? `${h2h.length} meeting${h2h.length > 1 ? "s" : ""} this season` : "First meeting this season"}</p>
          {s.model_series && (s.model_series.won + s.model_series.lost > 0) && <p className="mt-1 text-[11px]" data-testid="h2h-model-series">Model in this series <span className="font-mono-num font-bold text-emerald-400">{s.model_series.won}</span><span className="text-slate-600">–</span><span className="font-mono-num font-bold text-red-400">{s.model_series.lost}</span></p>}
        </div>
        <TeamFormCard row={home} side="home" total={s.teams_total} pts={!!s.points_system} sport={sport} />
      </div>
      {h2h.length > 0 && <H2HHistory h2h={h2h} home={home.team} away={away.team} />}
    </div>
  );
}

export function H2HHistory({ h2h, home, away }) {
  return (
    <div className="glass-card mt-3 overflow-hidden" data-testid="h2h-history">
      <p className="label-caps border-b border-[#26334D] px-4 py-2.5">Head-to-head this season <span className="float-right text-slate-600">model calls in each meeting</span></p>
      <ul className="divide-y divide-[#26334D]/60">
        {h2h.map((g) => (
          <li key={g.event_id} data-testid="h2h-row" className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
            <Link to={`/games/${g.event_id}`} className="font-mono-num w-12 shrink-0 text-xs text-slate-500 hover:text-emerald-300">{g.date.slice(5)}</Link>
            <span className="min-w-0 flex-1 truncate">
              <span className={g.winner === g.away ? "font-bold text-white" : "text-slate-400"}>{g.away}</span>
              <span className="font-mono-num mx-2 text-white">{g.away_score}–{g.home_score}</span>
              <span className={g.winner === g.home ? "font-bold text-white" : "text-slate-400"}>{g.home}</span>
              {!g.winner && <span className="ml-2 text-[10px] uppercase text-slate-500">draw</span>}
            </span>
            <span className="flex flex-wrap gap-1.5">
              {g.model.length ? g.model.map((m, i) => (
                <span key={i} data-testid="h2h-model-call" className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${m.result === "won" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : m.result === "lost" ? "border-red-500/40 bg-red-500/10 text-red-200" : "border-[#26334D] text-slate-400"}`}>
                  {m.selection} <span className="font-mono-num text-amber-400">{fmtOdds(m.odds)}</span> <span className="font-bold uppercase">{m.result === "won" ? "W" : m.result === "lost" ? "L" : "…"}</span>
                </span>
              )) : <span className="text-[11px] text-slate-600">No model call published</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
